import type { User } from 'firebase/auth';

export type ProfilePhotoUserData = {
  photoURL?: string | null;
  providerPhotoURL?: string | null;
} | null | undefined;

/** Google (or other OAuth) avatar from linked provider accounts. */
export function getProviderProfilePhotoURL(user: User | null): string | null {
  if (!user) return null;
  const google = user.providerData.find((p) => p.providerId === 'google.com');
  return google?.photoURL ?? user.photoURL ?? null;
}

export function isCustomProfilePhotoURL(url: string | null | undefined): boolean {
  if (!url) return false;
  return (
    url.includes('firebasestorage.googleapis.com') ||
    url.includes('storage.googleapis.com')
  );
}

/** Resolved avatar for UI: Firebase upload → live OAuth → stored copies → legacy photoURL. */
export function getProfilePhotoURL(
  user: User | null,
  userData: ProfilePhotoUserData,
  authPhotoURL?: string | null,
): string | null {
  const custom = userData?.photoURL;
  if (custom && isCustomProfilePhotoURL(custom)) {
    return custom;
  }

  // Prefer live OAuth URL — Firestore copies of Google avatars often go stale.
  const liveProvider =
    getProviderProfilePhotoURL(user) ?? authPhotoURL ?? user?.photoURL ?? null;
  if (liveProvider) return liveProvider;

  const storedProvider = userData?.providerPhotoURL ?? null;
  if (storedProvider) return storedProvider;

  if (custom) return custom;

  return null;
}

export function hasCustomProfilePhoto(userData: ProfilePhotoUserData): boolean {
  return isCustomProfilePhotoURL(userData?.photoURL);
}

export function isGoogleProfilePhotoURL(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('googleusercontent.com');
}

/** Upscale Google OAuth thumbnails for larger display surfaces (cards, profiles). */
export function getProfilePhotoURLForDisplay(
  url: string | null | undefined,
  targetPx = 512,
): string | null {
  if (!url) return null;
  if (!isGoogleProfilePhotoURL(url)) return url;

  const size = Math.min(Math.max(targetPx, 64), 800);

  if (/=s\d+(-c)?/.test(url)) {
    return url.replace(/=s\d+(-c)?/, `=s${size}$1`);
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('sz', String(size));
    return parsed.toString();
  } catch {
    return url;
  }
}

export function profilePhotoImgProps(
  url: string | null | undefined,
  targetPx = 512,
): { src: string; referrerPolicy?: 'no-referrer' } | null {
  const src = getProfilePhotoURLForDisplay(url, targetPx);
  if (!src) return null;
  return isGoogleProfilePhotoURL(src)
    ? { src, referrerPolicy: 'no-referrer' as const }
    : { src };
}
